import { format } from "date-fns";

// Pure helpers for the Service advisor performance tab. The per-advisor figures
// come from three analytics endpoints; /tat and /escalation-rate key an advisor by
// the last 10 digits of their phone, and /attention falls back to the name when
// there is no number. They are joined here into one row per advisor.

// Cars with no advisor are not a person to compare: /attention keys them
// "unassigned" and the other endpoints leave the key empty.
const isUnassigned = (key) => !key || key === "unassigned";

const byKey = (rows, key) => new Map((rows || []).map((r) => [r[key], r]));

export function advisorRows({ attention, tat, escalation }) {
  const waiting = byKey(attention?.awaiting_reply?.advisors, "key");
  const repair = byKey(tat?.rows, "dim_key");
  const escalated = byKey(escalation?.rows, "key");

  const keys = new Set([...waiting.keys(), ...repair.keys(), ...escalated.keys()]);
  const rows = [];
  for (const key of keys) {
    if (isUnassigned(key)) continue;
    const w = waiting.get(key);
    const t = repair.get(key);
    const e = escalated.get(key);
    rows.push({
      key,
      name: w?.name || t?.dim_label || e?.label || key,
      workshop: w?.workshop || "",
      duplicate: !!w?.duplicate,
      groups: w?.groups ?? e?.groups ?? 0,
      unanswered: w?.unanswered ?? 0,
      notJoined: w?.not_joined ?? 0,
      saNotJoined: w?.sa_not_joined ?? 0,
      repairHours: t?.mean_hours ?? null,
      repaired: t?.n ?? 0,
      escalationPct: e?.rate_pct ?? null,
    });
  }
  return rows;
}

// Escalation rate over everyone: the share of all groups that escalated, worked
// out from each advisor's rate and group count rather than averaging the rates.
export function overallEscalationPct(escalation) {
  const rows = escalation?.rows || [];
  const groups = rows.reduce((a, r) => a + (r.groups || 0), 0);
  if (!groups) return null;
  const escalated = rows.reduce((a, r) => a + Math.round(((r.rate_pct || 0) * (r.groups || 0)) / 100), 0);
  return Math.round((escalated * 1000) / groups) / 10;
}

export function summarize(rows, { tat, escalation }) {
  return {
    advisors: rows.filter((r) => r.groups > 0).length,
    repairHours: tat?.overall?.mean_hours ?? null,
    escalationPct: overallEscalationPct(escalation),
    unanswered: rows.reduce((a, r) => a + r.unanswered, 0),
  };
}

// Sort by one column. An advisor with nothing to measure always goes last,
// whichever way the column is sorted.
export function sortRows(rows, key, dir = "desc") {
  const sign = dir === "asc" ? 1 : -1;
  return rows.slice().sort((a, b) => {
    const x = a[key];
    const y = b[key];
    if (x == null && y == null) return a.name.localeCompare(b.name);
    if (x == null) return 1;
    if (y == null) return -1;
    const d = typeof x === "string" ? x.localeCompare(y) : x - y;
    return d ? d * sign : a.name.localeCompare(b.name);
  });
}

// ---- the single-advisor panel ----------------------------------------------------

// A length of time as "45m", "5h 12m" or "2d 4h".
export function waitingFor(hours) {
  if (hours == null || Number.isNaN(Number(hours))) return "–";
  const minutes = Math.round(Number(hours) * 60);
  if (minutes < 60) return `${minutes}m`;
  const days = Math.floor(minutes / 1440);
  const h = Math.floor((minutes % 1440) / 60);
  return days ? `${days}d ${h}h` : `${h}h ${minutes % 60}m`;
}

// One person's place in one group: "Not joined · 2d 4h" (since the group was
// created), "Joined 6h ago" (since they joined; just "Joined" when no join event
// was recorded) or "No number".
export function joinText(side) {
  if (!side) return "–";
  if (side.status === "not_joined") return `Not joined · ${waitingFor(side.hours)}`;
  if (side.status === "no_number") return "No number";
  return side.hours == null ? "Joined" : `Joined ${waitingFor(side.hours)} ago`;
}

// How far a group is through the service journey, from the desk's resolved steps:
// the steps done out of the steps this group has, and the furthest one done.
export function stepProgress(steps) {
  const list = steps || [];
  const done = list.filter((s) => s.done);
  return { done: done.length, total: list.length, latest: done.length ? done[done.length - 1].label : "" };
}

export function joinSummary(groups) {
  const list = groups || [];
  return {
    cars: list.length,
    customers: list.filter((g) => g.customer.status === "not_joined").length,
    advisors: list.filter((g) => g.advisor.status === "not_joined").length,
  };
}

// The first and last day a group was created, as "21 Oct - 3 Nov" ("21 Oct" when
// it was a single day, "" when there are no groups).
export function createdRange(groups) {
  const days = (groups || [])
    .filter((g) => g.created_at)
    .map((g) => new Date(g.created_at))
    .filter((d) => !Number.isNaN(d.getTime()))
    .sort((a, b) => a - b);
  if (!days.length) return "";
  const first = format(days[0], "d MMM");
  const last = format(days[days.length - 1], "d MMM");
  return first === last ? first : `${first} - ${last}`;
}
